import { UIButton, UICheckButton, UIControl, UILabel, UIViewController, UIView } from "uikit";
import { BookingProductReviewViewController } from "./BookingProductReviewViewController";
import { BookingClientProtocolDelegate, BookingClientViewController } from "./BookingClientViewController";
import { BookingStep, StepView, bookingStepsOf, isBookingStep } from "./StepView";
import { BookingExtrasViewController } from "./BookingExtrasViewController";
import { DLDB, RIGHTS_URL } from "dldb";
import { NSLocalizeString } from "foundation";
import { MIOStringWithFormat } from "mio-core";
import { AdyenPaymentViewController } from "./AdyenPaymentViewController";
import { BookingSummaryViewController } from "./BookingSummaryViewController";
import { MIOCoreGetPlatformLanguage } from "mio-core";
import { IBook } from "dldb";

export class BookingReviewViewController extends UIViewController implements BookingClientProtocolDelegate
{
    private productReviewView: UIView = null;
    private extrasView: UIView = null;
    private clientView: UIView = null;

    // First step number of this page - 1: set by the presenter so the pills continue the booking
    // page numbering (4 steps there makes the client info here step 5).
    public stepNumberOffset = 0;
    private steps: BookingStep[] = [];
    
    protected errorLbl        : UILabel       = null ;

    private acceptTermsCB     : UICheckButton = null ;
    private acceptPrivacyCB   : UICheckButton = null ;
    private acceptPublicityCB : UICheckButton = null ;
    private acceptTermsLbl    : UILabel       = null ;
    private acceptPrivacyLbl  : UILabel       = null ;
    
    private bookButton: UIButton = null;
    private backButton: UIButton = null;
    private payLoading: UIView   = null;   // optional spinner inside the reserve button


    viewDidLoad() {
        super.viewDidLoad();

        if ( DLDB.shared.cart().showExtrasController() ) {
            let extrasViewController = new BookingExtrasViewController();
            extrasViewController.initWithNibName("BookingExtrasView");
            this.extrasView.addSubview( extrasViewController.view );
            this.addChildViewController( extrasViewController );
        }
        else {
            this.extrasView.hidden = true;
        }

        let productReviewViewController = new BookingProductReviewViewController();
        productReviewViewController.initWithNibName("BookingProductReviewView");
        this.productReviewView.addSubview( productReviewViewController.view );
        this.addChildViewController( productReviewViewController );

        let clientReviewViewController = new BookingClientViewController();
        clientReviewViewController.initWithNibName("BookingClientView");
        clientReviewViewController.delegate = this;
        this.clientView.addSubview( clientReviewViewController.view );
        this.addChildViewController( clientReviewViewController );

        this.setupSteps();

        if ( DLDB.shared.combineTermsAndPrivacy ) {
            // One line for both: the single checkbox drives acceptTerms AND acceptPrivacy
            if ( this.acceptTermsLbl )
                this.acceptTermsLbl.text = MIOStringWithFormat( NSLocalizeString( "ACCEPT TERMS AND PRIVACY", "ACCEPT TERMS AND PRIVACY" ), DLDB.shared.getURL( RIGHTS_URL.TERMS ), DLDB.shared.getURL( RIGHTS_URL.PRIVACY ) ) ;
            if ( this.acceptPrivacyCB ) this.acceptPrivacyCB.hidden = true;
            if ( this.acceptPrivacyLbl ) this.acceptPrivacyLbl.hidden = true;
        }
        else {
            if ( this.acceptPrivacyLbl )
                this.acceptPrivacyLbl.text = MIOStringWithFormat( NSLocalizeString( "ACCEPT PRIVACY", "ACCEPT PRIVACY" ), DLDB.shared.getURL( RIGHTS_URL.PRIVACY ) ) ;

            if ( this.acceptTermsLbl )
                this.acceptTermsLbl.text = MIOStringWithFormat( NSLocalizeString( "ACCEPT TERMS", "ACCEPT TERMS" ), DLDB.shared.getURL( RIGHTS_URL.TERMS ) ) ;
        }
        if ( this.acceptTermsCB )
            this.acceptTermsCB.addTarget( this, this.checkBookBtn, UIControl.Event.touchUpInside );

        if ( this.acceptPrivacyCB)
            this.acceptPrivacyCB.addTarget( this, this.checkBookBtn, UIControl.Event.touchUpInside );

        if ( this.acceptPublicityCB)
            this.acceptPublicityCB.addTarget( this, this.checkBookBtn, UIControl.Event.touchUpInside );

        this.errorLbl.setHidden( true ) ;

        this.backButton.addTarget(this, this.onBack, UIControl.Event.touchUpInside);
        
        this.bookButton.addTarget(this, this.onBook, UIControl.Event.touchUpInside);
        this.bookButton.enabled = false;
    }

    /**
     * Steps of the review page, same rules as the booking page: only children conforming to
     * BookingStep get a numbered pill (the product review is a summary, so its pill hides), the
     * numbers continue from the booking page, and the highlight sits on the first step without a
     * value — the client info, until its form is complete.
     */
    private setupSteps ( ) {
        this.steps = bookingStepsOf( this.childViewControllers );
        this.refreshStepViews();

        // Step layouts load asynchronously (nib XHR): refresh again as each child finishes, both
        // to number the real pills and to hide the ones of the summary children.
        for ( const child of this.childViewControllers ) {
            const vc = child as UIViewController;
            const original = vc.viewDidLoad.bind( vc );
            vc.viewDidLoad = () => { original(); this.refreshStepViews(); };
        }
    }

    private refreshStepViews ( ) {
        // No BookingStep conformance means no badge: hide the pill of summary children
        for ( const vc of this.childViewControllers ) {
            if ( isBookingStep( vc ) ) continue;
            const stepView = ( vc as any ).stepView as StepView;
            if ( stepView != null ) stepView.hidden = true;
        }

        let current = -1;
        for ( let i = 0 ; i < this.steps.length ; i++ ) {
            if ( !this.steps[ i ].hasValue() ) { current = i; break; }
        }

        for ( let i = 0 ; i < this.steps.length ; i++ ) {
            const stepView = this.steps[ i ].stepView;
            if ( stepView == null ) continue;
            stepView.number = this.stepNumberOffset + i + 1;
            stepView.selected = ( i === current );
        }
    }

    onClientFormDidChange ( vc: BookingClientViewController ) {
        this.refreshStepViews();
    }

    checkBookBtn = ( dryRun: boolean = false ) => {
        // Combined mode: the terms checkbox also accepts the privacy policy
        if ( DLDB.shared.combineTermsAndPrivacy && this.acceptPrivacyCB ) this.acceptPrivacyCB.on = this.acceptTermsCB.on;

        const bookBtn = this.bookButton ;

       if ( bookBtn )
            bookBtn.enabled =  this.acceptTermsCB.on
                            && this.acceptPrivacyCB.on ;
    }

    onBook() {
        const products = DLDB.shared.cart().lines.length > 0 ?
            DLDB.shared.cart().lines.map(l => ({ productID: l.productID, quantity: l.quantity })) :
            [];

        let b: IBook = {
            id: DLDB.shared.booking().id.UUIDString,
            transactionID: DLDB.shared.booking().transactionID,
            pax: DLDB.shared.cart().effectivePax,
            zoneID: DLDB.shared.cart().zoneID,
            timeID: DLDB.shared.cart().time,
            date: DLDB.shared.cart().date,
            comments: DLDB.shared.cart().comments,
            client: {
                name: DLDB.shared.cart().name,
                phone: DLDB.shared.cart().phone,
                email: DLDB.shared.cart().email,
            },
            acceptTerms: this.acceptTermsCB.on,
            acceptPrivacy: this.acceptPrivacyCB.on,
            acceptPublicity: this.acceptPublicityCB.on,
            language: MIOCoreGetPlatformLanguage()
        };

        if (products.length > 0) {
            b.products = products;
        }
        if ( DLDB.shared.cart().eventID ) {
            b.eventID = DLDB.shared.cart().eventID;
        }

        // transactionID is fixed for this page load: if the request is repeated (retry, double
        // click) the server recognises it and answers as the first time instead of booking twice.
        this.setSending( true );
        DLDB.shared.book( b, ( code, json ) => {
            this.setSending( false );
            if ( code === 200 && json["error"] == null )
            {
                if ( json.data["PaymentFields"] != null ) {

                    let vc = new AdyenPaymentViewController();
                    vc.initWithNibName( "AdyenPaymentView" );
                    vc.setPaymentFields( json.data["PaymentFields"] );
                    this.presentViewController(vc, true);
                }
                else {
                    const vc = BookingSummaryViewController.newInstance( true );
                    this.presentViewController(vc, true);
                }
            }
            else 
            {
                let error = (json.error || "").replace( "[BookingApiError] ", "" );
                this.errorLbl.text = NSLocalizeString(error, error) ;
                this.errorLbl.setHidden( false ) ;
            }
        } );
    }

    onBack() {
        this.dismissViewController(true);
    }

    // Reserve pressed: spinner on, buttons off until the server answers
    private setSending ( sending: boolean ) {
        if ( this.payLoading ) this.payLoading.hidden = !sending;
        this.bookButton.enabled = !sending;
        this.backButton.enabled = !sending;
    }
}